import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, User, Menu, Sun, Moon, Film, LayoutDashboard, LogOut, X, Heart } from 'lucide-react';
import { useState } from 'react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

export function Navbar() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const links = [
    { to: '/shop', label: 'Collection' },
    { to: '/sets', label: 'Sets' },
    { to: '/presale', label: 'Presale' },
    { to: '/reels', label: 'Films' },
  ];

  return (
    <header className="glass sticky top-0 z-50 border-b border-border">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto flex items-center justify-between h-20">
        <button data-testid="mobile-menu-button" className="md:hidden text-foreground" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>

        <Link to="/" data-testid="navbar-brand-logo" className="flex items-center gap-2 group">
          <span className="font-script text-3xl sm:text-4xl text-gold leading-none pb-1 group-hover:text-gold-hover transition-colors">Ke-Sa</span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-sm tracking-wide text-muted-foreground hover:text-gold transition-colors">
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button data-testid="theme-toggle" onClick={toggle} className="text-muted-foreground hover:text-gold transition-colors">
            {theme === 'dark' ? <Sun size={19} /> : <Moon size={19} />}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="navbar-user-menu" className="text-muted-foreground hover:text-gold transition-colors outline-none">
                <User size={19} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 bg-surface-elevated border-border">
                <div className="px-2 py-1.5 text-xs text-muted-foreground truncate">{user.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-account" onClick={() => navigate('/account')} className="cursor-pointer">
                  <User size={15} className="mr-2" /> My Account
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-wishlist" onClick={() => navigate('/wishlist')} className="cursor-pointer">
                  <Heart size={15} className="mr-2" /> Wishlist
                </DropdownMenuItem>
                {user.role === 'admin' && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate('/admin')} className="cursor-pointer">
                    <LayoutDashboard size={15} className="mr-2" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-reels" onClick={() => navigate('/reels')} className="cursor-pointer">
                  <Film size={15} className="mr-2" /> Films
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate('/'); }} className="cursor-pointer text-destructive">
                  <LogOut size={15} className="mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/auth" data-testid="navbar-login-link" className="text-muted-foreground hover:text-gold transition-colors">
              <User size={19} />
            </Link>
          )}

          <button data-testid="navbar-cart-button" onClick={() => setOpen(true)} className="relative text-muted-foreground hover:text-gold transition-colors">
            <ShoppingBag size={19} />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 bg-gold text-primary-foreground text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-mono">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-border px-6 py-4 flex flex-col gap-4 bg-surface">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="text-sm text-muted-foreground hover:text-gold">
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}
